import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getUsers, updateUserRoles } from '@/lib/api-calls';
import { QueryKeys } from '@/lib/queries';
import { CreateCoach } from '@/routes/_layout/users/-components/create-coach';
import { coach, distinct, roleHash } from '@airreading/common';
import { UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

export interface PromoteCoachProps {
  trigger: ReactNode;
  user: UserWithRolesAndProfiles;
}

export const PromoteCoach = ({ trigger, user }: PromoteCoachProps) => {
  const [open, setOpen] = useState(false);
  const { mutateAsync: updateUserRolesMutate, isPending } = useMutation({
    mutationFn: updateUserRoles,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = () => {
    return updateUserRolesMutate({
      userId: user.id,
      roles: [...user.userRoles.map(({ role, schoolId }) => ({ role, schoolId })), coach()].filter(distinct(roleHash)),
    })
      .then(() => {
        toast({
          title: 'User teacher role granted',
        });
        setOpen(false);
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
      })
      .catch(() => {
        toast({
          title: 'Teacher promotion failed',
        });
      });
  };

  const alreadyHasCoachRole = !!user.coachProfile;

  if (!alreadyHasCoachRole) {
    return (
      <>
        <CreateCoach trigger={trigger} fromUser={user} />
      </>
    );
  }

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign teacher role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to assign {user.fullNameOrEmail} as a teacher?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button variant="destructive" disabled={isPending} onClick={onSubmit}>
                Confirm
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
